import React, { useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Shared micro-components
// ─────────────────────────────────────────────────────────────────────────────

const Section = ({ icon, title, sectionKey, expanded, onToggle, children }) => {
  const isOpen = expanded[sectionKey];
  return (
    <div style={{
      borderRadius: 10,
      overflow: 'hidden',
      border: isOpen ? '1px solid #4f46e5' : '1px solid rgba(255,255,255,0.06)',
      marginBottom: 10,
      background: isOpen ? 'rgba(79,70,229,0.07)' : 'rgba(255,255,255,0.02)',
      transition: 'border-color 0.2s, background 0.2s',
    }}>
      <button
        onClick={() => onToggle(sectionKey)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer',
          color: isOpen ? '#c4b5fd' : 'rgba(255,255,255,0.7)',
          fontWeight: 700, fontSize: 13, letterSpacing: '0.3px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15 }}>{icon}</span>{title}
        </span>
        <span style={{ fontSize: 10, opacity: 0.6, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
      </button>
      {isOpen && (
        <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 5 }}>
      {label}
    </div>
    {children}
  </div>
);

const styledInput = {
  width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 7, padding: '9px 11px', fontSize: 13, color: '#e2e8f0',
  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};

const TextInput = ({ value, placeholder, onChange, rows }) =>
  rows
    ? <textarea value={value} placeholder={placeholder} onChange={onChange} style={{ ...styledInput, resize: 'vertical', minHeight: 56 }} rows={rows} />
    : <input type="text" value={value} placeholder={placeholder} onChange={onChange} style={styledInput} />;

const Slider = ({ label, min, max, value, onChange, unit = '' }) => (
  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.06)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#818cf8', background: 'rgba(79,70,229,0.2)', padding: '2px 7px', borderRadius: 4 }}>{value}{unit}</span>
    </div>
    <input type="range" min={min} max={max} value={value} onChange={e => onChange(parseInt(e.target.value))} style={{ width: '100%', accentColor: '#6366f1' }} />
  </div>
);

const Toggle = ({ label, checked, onChange, sublabel }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '4px 0' }}>
    <div onClick={() => onChange(!checked)} style={{
      width: 36, height: 20, borderRadius: 10, flexShrink: 0,
      background: checked ? '#6366f1' : 'rgba(255,255,255,0.12)',
      position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
    }}>
      <div style={{ position: 'absolute', top: 3, left: checked ? 18 : 3, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
    </div>
    <div>
      <div style={{ fontSize: 13, color: checked ? '#e2e8f0' : 'rgba(255,255,255,0.55)', fontWeight: 600 }}>{label}</div>
      {sublabel && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>{sublabel}</div>}
    </div>
  </label>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const ControlPanels = ({
  company = '', jobTitle = '', location = '', supportingText = '',
  topTitle = '', taglineText = '', ctaText = '', postedDate = '', salary = '',
  showChips = true, showTitle = false, showTagline = true, showCta = true,
  showBrand = false, showLogo = false, showDate = false, showSalary = false,
  chipStyle = 1, logoType = 'logo', forceWhiteLogo = true,
  backgroundImage = '', backgroundSize = 100, backgroundPositionX = 50,
  backgroundPositionY = 50, backgroundOverlayOpacity = 28,
  backgrounds = [], aiBackgrounds = [],
  headingSize = 36, bodySize = 22, padSize = 40, logoSize = 24,
  textColor = '#ffffff',
  theme = 'light', logoX = 20, logoY = 12,
  onContentChange = () => {}, onDisplayChange = () => {}, onTypographyChange = () => {},
  onBackgroundChange = () => {}, onBackgroundStyleChange = () => {},
  onLogoChange = () => {}, onLogoPositionChange = () => {},
  onExport = () => {}, onExportAll = () => {},
  onCaptureForInstagram = null, activeDraftId = null,
}) => {
  const [expanded, setExpanded] = useState({ content: true, typography: false, display: false, assets: false });
  const [aiGridOpen, setAiGridOpen] = useState(true);

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  return (
    <div style={{
      background: '#0d1117', borderRadius: 14, padding: 16,
      border: '1px solid rgba(255,255,255,0.07)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* Export */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onExport} style={{
          flex: 1, padding: '11px 0', background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
          border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
        }}>↓ Export</button>
        <button onClick={onExportAll} style={{
          flex: 1, padding: '11px 0', background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
          color: 'rgba(255,255,255,0.7)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
        }}>Export All</button>
        {onCaptureForInstagram && (
          <button onClick={onCaptureForInstagram} style={{
            width: '100%', padding: '11px 0',
            background: 'linear-gradient(135deg,#e1306c,#833ab4)',
            border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            marginTop: 2,
          }}>📷 Capture for Instagram</button>
        )}
      </div>

      {/* ── Content ── */}
      <Section icon="✏️" title="Content" sectionKey="content" expanded={expanded} onToggle={toggle}>
        <Field label="Company"><TextInput value={company} placeholder="Company name" onChange={e => onContentChange('company', e.target.value)} /></Field>
        <Field label="Job Title"><TextInput value={jobTitle} placeholder="Job title" onChange={e => onContentChange('jobTitle', e.target.value)} /></Field>
        <Field label="Location"><TextInput value={location} placeholder="Location" onChange={e => onContentChange('location', e.target.value)} /></Field>
        <Field label="Salary"><TextInput value={salary} placeholder="e.g. £25k–£30k or £15/hr" onChange={e => onContentChange('salary', e.target.value)} /></Field>
        <Field label="Supporting Text"><TextInput value={supportingText} placeholder="Additional detail…" onChange={e => onContentChange('supportingText', e.target.value)} rows={2} /></Field>
      </Section>

      {/* ── Typography ── */}
      <Section icon="🔤" title="Typography & Layout" sectionKey="typography" expanded={expanded} onToggle={toggle}>
        <Slider label="Title Size" min={18} max={120} value={headingSize} unit="px" onChange={v => onTypographyChange('headingSize', v)} />
        <Slider label="Body Size" min={10} max={36} value={bodySize} unit="px" onChange={v => onTypographyChange('bodySize', v)} />
        <Slider label="Padding" min={16} max={200} value={padSize} unit="px" onChange={v => onTypographyChange('padSize', v)} />
        <Field label="Text Colour">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {['#ffffff', '#111111', '#1e293b', '#f0fdf4', '#fef9c3', '#fce7f3', '#e0f2fe', '#f5f3ff'].map(c => (
              <button key={c} onClick={() => onTypographyChange('textColor', c)} title={c} style={{
                width: 26, height: 26, borderRadius: '50%', background: c, border: textColor === c ? '3px solid #6366f1' : '2px solid rgba(255,255,255,0.2)',
                cursor: 'pointer', flexShrink: 0, boxSizing: 'border-box',
              }} />
            ))}
            <input type="color" value={textColor} onChange={e => onTypographyChange('textColor', e.target.value)}
              title="Custom colour" style={{ width: 26, height: 26, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', cursor: 'pointer', padding: 1, background: 'none' }} />
          </div>
        </Field>
      </Section>

      {/* ── Display ── */}
      <Section icon="👁" title="Display Elements" sectionKey="display" expanded={expanded} onToggle={toggle}>
        <Toggle label="Company Pills" checked={showChips} onChange={v => onDisplayChange('showChips', v)} />
        <Toggle label="Salary Badge" checked={showSalary} onChange={v => onDisplayChange('showSalary', v)} />
        <Toggle label="CTA Button" checked={showCta} onChange={v => onDisplayChange('showCta', v)} sublabel={ctaText || 'e.g. Try Free for 60 Days'} />
        {showCta && <TextInput value={ctaText} placeholder="CTA text" onChange={e => onContentChange('ctaText', e.target.value)} />}
        <Toggle label="Top Title" checked={showTitle} onChange={v => onDisplayChange('showTitle', v)} sublabel={topTitle || 'e.g. Now Hiring'} />
        {showTitle && <TextInput value={topTitle} placeholder="Now Hiring…" onChange={e => onContentChange('topTitle', e.target.value)} />}
        <Toggle label="Tagline" checked={showTagline} onChange={v => onDisplayChange('showTagline', v)} />
        {showTagline && <TextInput value={taglineText} placeholder="Tagline text…" onChange={e => onContentChange('taglineText', e.target.value)} rows={2} />}
      </Section>

      {/* ── Assets & Background ── */}
      <Section icon="🖼" title="Assets & Background" sectionKey="assets" expanded={expanded} onToggle={toggle}>

        {/* AI Library grid */}
        {aiBackgrounds.length > 0 && (
          <div>
            <button
              onClick={() => setAiGridOpen(p => !p)}
              style={{
                width: '100%', background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)',
                borderRadius: 8, padding: '8px 12px', color: '#c4b5fd', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
              }}
            >
              <span>✨ AI Library ({aiBackgrounds.length})</span>
              <span style={{ opacity: 0.6, transform: aiGridOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {aiGridOpen && (
              <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
                  {aiBackgrounds.map(img => {
                    const active = backgroundImage === img.url;
                    return (
                      <button
                        key={img.id}
                        onClick={() => { onBackgroundChange(img.url); }}
                        title={img.label}
                        style={{
                          padding: 0, background: 'none', cursor: 'pointer',
                          border: active ? '2px solid #7c3aed' : '2px solid rgba(255,255,255,0.1)',
                          borderRadius: 8, overflow: 'hidden', aspectRatio: '1/1',
                          position: 'relative', boxShadow: active ? '0 0 0 1px #7c3aed' : 'none',
                          transition: 'border-color 0.15s, transform 0.1s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <img src={img.url} alt={img.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        {active && (
                          <div style={{ position: 'absolute', inset: 0, background: 'rgba(124,58,237,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>✓</span>
                          </div>
                        )}
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent,rgba(0,0,0,0.78))', padding: '14px 4px 4px', fontSize: 9, color: '#e5e7eb', lineHeight: 1.2 }}>
                          {img.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preset backgrounds */}
        {backgrounds.length > 0 && (
          <Field label="Preset Backgrounds">
            <select value={backgroundImage} onChange={e => onBackgroundChange(e.target.value ? `/Background/${e.target.value}` : '')}
              style={{ ...styledInput }}>
              <option value="">— Default gradient —</option>
              {backgrounds.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
        )}

        {/* Upload */}
        <Field label="Upload Image">
          <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '10px', background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)',
            borderRadius: 8, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.5)',
          }}>
            📁 Choose file
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { if (e.target.files?.[0]) onBackgroundChange(URL.createObjectURL(e.target.files[0])); }} />
          </label>
        </Field>

        {backgroundImage && (
          <>
            <Slider label="BG Size" min={30} max={200} value={backgroundSize} unit="%" onChange={v => onBackgroundStyleChange('backgroundSize', v)} />
            <Slider label="Pos X" min={0} max={100} value={backgroundPositionX} unit="%" onChange={v => onBackgroundStyleChange('backgroundPositionX', v)} />
            <Slider label="Pos Y" min={0} max={100} value={backgroundPositionY} unit="%" onChange={v => onBackgroundStyleChange('backgroundPositionY', v)} />
            <Slider label="Overlay Strength" min={0} max={200} value={backgroundOverlayOpacity} unit="%" onChange={v => onBackgroundStyleChange('overlayOpacity', v)} />
            <button onClick={() => onBackgroundChange('')} style={{
              width: '100%', padding: '9px', background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.3)', borderRadius: 7, color: '#fca5a5', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>✕ Clear Background</button>
          </>
        )}

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 0' }} />

        {/* Logo */}
        <Field label="Upload Logo">
          <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '10px', background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)',
            borderRadius: 8, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.5)',
          }}>
            🖼 Upload logo
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onLogoChange(e.target.files?.[0] || null)} />
          </label>
        </Field>
        <Toggle label="Show Logo" checked={showLogo} onChange={v => onDisplayChange('showLogo', v)} />
        {showLogo && (
          <>
            <Toggle label="Force White" checked={forceWhiteLogo} onChange={v => onDisplayChange('forceWhiteLogo', v)} sublabel="Invert logo to white on dark BG" />
            <Slider label="Logo Size" min={20} max={180} value={logoSize} unit="px" onChange={v => onTypographyChange('logoSize', v)} />
            <Slider label="Logo X" min={0} max={80} value={logoX} unit="px" onChange={v => onLogoPositionChange('x', v)} />
            <Slider label="Logo Y" min={-50} max={200} value={logoY} unit="px" onChange={v => onLogoPositionChange('y', v)} />
            <button onClick={() => onLogoChange(null)} style={{
              width: '100%', padding: '9px', background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.3)', borderRadius: 7, color: '#fca5a5', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>✕ Clear Logo</button>
          </>
        )}
      </Section>
    </div>
  );
};

export default ControlPanels;
